import { IShareScenario } from "common/lib";
import { nanoid } from "@reduxjs/toolkit";
import { BlobReader, BlobWriter, TextReader, ZipWriter } from "@zip.js/zip.js";
import { CREATE_SCENARIO, LIST_SCENARIOS } from "./gql";
import { AssetTypeEnum, Scenario as GQLscenario } from "../../../cms-glue/gql";
import { ApolloGQLClient } from "../ApolloGQLClient";
import Scenario from "../../../scenarioElements/Scenario";
import config from "../../../../config";
import store, { StoreActions } from "../../../../state/store";
import Metadata from "../../../MetaData";
import QuizManager from "../../../QuizManager";
import GQLprojects from "../projects/GQLprojects";
import { GrapesRenderModes, IGrapesJSProjectData } from "../../../../interfaces/VisageUI";
import { RenderGrapesToHTML } from "../../../../components/visageUI/GrapesJSUtilities";
import GQLassets from "../assets/GQLassets";
import { GQLSession } from "../GQLSession";

interface IGQLScenario {
	id: string
	databaseId: string
	name: string
	description: any
	version: number
	createdAt: string
	updatedAt: string
	latestRelease: any
	originGroup: {
		id: string
		name: string
	}
	organization: {
		id: string
		databaseId: string
	}
	recipientGroups: {
		nodes: Array<{
			id: string
			name: string
		}>
	}
	permissions: {
		canUpdate: {
			value: boolean
		}
	}
}

export default class GQLScenario
{   
	private static gqlAfterIndex = (pageSize: number, page: number) => 
	{
		return pageSize * page - pageSize - 1;
	};

	static async listScenarios(page = 1, args = {})
	{
		const variables = {
			first: 100,
			afterIndex: this.gqlAfterIndex(100, page),
			scenariosQueryArguments: {
				...args
			}
		}

		const data = await ApolloGQLClient.query(LIST_SCENARIOS, variables, 'request to list scenarios');

		console.info({
			data
		}, 'response to list projects request');

		const scenarios: IGQLScenario[] = data.scenarios.nodes;
		return scenarios;
	}

	static async getStepUIData()
	{
	// render all UI data into separate HTML files, and zip them together
		const uiDataEntries = Object.entries<IGrapesJSProjectData>(store.getState().visageModule.stepUIData);
		const htmlFiles = new Map<string, string>();

		for await(const [key, value] of uiDataEntries)
		{
			const step = Scenario.getStep(key);
			if (!step || step.isDeleted()) continue;

			if(!value || !Object.keys(value).length) continue;

			const renderedHTML = await RenderGrapesToHTML(value, GrapesRenderModes.Publish);
			htmlFiles.set(key, renderedHTML);
		}

		// add the zip file to the request
		return htmlFiles;
	}

	static async createScenario(projectId: string, name: string, description = '')
	{
		const variables = {
			input: {
				scenario: {
					name,
					description,
					projectId
				}
			}
		};

		const data = await ApolloGQLClient.mutate<object>(CREATE_SCENARIO, variables, 'request to create a scenario');

		const scenario: GQLscenario = data.createScenario.scenario;

		console.info({
			scenarioId: scenario.databaseId,
			name: name,
			description: description,
			projectId: projectId,
			data
		}, 'response to create a scenario');

		return scenario;
	}

	static getTime() 
	{
		const date = new Date();
		let day = String(date.getDate());
		let month = String(date.getMonth() + 1);
		const year = String(date.getFullYear());
		let hour = String(date.getHours());
		let minute = String(date.getMinutes());
		let second = String(date.getSeconds());
		const miliseconds = String(date.getMilliseconds());
	
		day = day.length === 1 ? '0' + day : day;
		month = month.length === 1 ? '0' + month : month;
		hour = hour.length === 1 ? '0' + hour : hour;
		minute = minute.length === 1 ? '0' + minute : minute;
		second = second.length === 1 ? '0' + second : second;
	
		return year + '-' + month + '-' + day + "T" + hour + minute + second + '.' + miliseconds;
	}

	static createMetadataFile(scenarioName: string, zipFileSize: number, createVersion: string,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		extensionsVersion: string, sessionType: unknown, addedMetadata: any = {}) 
	{
		const metadata = {
			"scope_file_size": zipFileSize,
			"scenario_name": scenarioName,
			"authoring_platform_version": createVersion,
			"scope_extension_version": extensionsVersion,
			"session_type": sessionType,
			"published_at": this.getTime(),
			...addedMetadata
		};
	
		return JSON.stringify(metadata, null, "\t");
	}

	static async downloadFileToBlob(url: string)
	{
		const response = await fetch(url);
		if (!response.ok) 
		{
			throw new Error(`Failed to download ${url}: ${response.statusText}`);
		}
		return await response.blob();
	}

	static publishProject = async (blob: Blob, encrypted = false, recipientGroups: string[] = [],
		publishType, assetsIds: string[] = [], additionalBlobMap = {},
		resourcePermissions: IShareScenario["replaceResourcePermissions"]) =>
	{
		const currentScenario = Scenario.current();
		if(!currentScenario)
		{
			console.error("No current scenario found for publishing");
			return;
		}
	
		const extras = {}
		if(QuizManager.getQuizType())
		{
			extras["quizSettings"] = {...store.getState().checklistModule.quizSettings};
			extras["checklistData"] = {...QuizManager.packQuizReduxData()};
		}
		
		// 	// parse recipient groups
		const projectId = currentScenario.id;
		const recipientGroupIds = recipientGroups;
		const description = 'WL Create 2.0 Scenario';
		const createVersion = config.CREATE_VERSION;
		const extensionsVersion = config.SCOPE_EXTENSION_VERSION || '2.0.0';
		const projectName = currentScenario.name || 'No name';
		const externalData = {extras: {...extras}};
		const addedMetadata = Metadata.calculateProjectMetadata(blob.size) || {};

		// check if publish type is a string, if so change to integer
		if (typeof publishType === 'string') 
		{
			publishType = parseInt(publishType);
		}

		const project = await GQLprojects.getProjectByID({ databaseId: projectId });
	
		if (!project) 
		{
			throw new Error('Project not found');
		}
	
		let cmsScenario: GQLscenario;
	
		if (project.scenarios && project.scenarios.length > 0) 
		{
			cmsScenario = project.scenarios[0];
		}
		else 
		{
			cmsScenario = await this.createScenario(project.id, project.name as string, description);
	
			if (!cmsScenario?.databaseId) 
			{
				throw new Error('There was an error creating the scenario');
			}
		}
	   
		// create metadata file
		const metaDataFile = this.createMetadataFile(
			projectName, blob.size, createVersion, extensionsVersion, publishType, addedMetadata
		);

		// move glb to a temporary location
		const blobWriter = new BlobWriter("application/zip");
		const viewer = await GQLSession.getViewer(); 
		const zipWriteOptions = { 
			bufferedWrite: false,
			password:encrypted ? viewer.encryptionKey || undefined : undefined,
			level: 8
		};
		const zipWriter = new ZipWriter(blobWriter, zipWriteOptions);

		await GQLScenario.addFileToZip(zipWriter, assetsIds, additionalBlobMap, metaDataFile, blob);		
		await zipWriter.close();
		const newBlob = await blobWriter.getData();

		// publish
		console.info({
			cms_scenario_id: cmsScenario.databaseId, groups: recipientGroupIds,
			create_version: createVersion, extensions_version: extensionsVersion,
			publish_type: publishType
		}, "requesting the CMS to publish a project file");

		const fileName = encrypted ? 'scenario.scope.encrypted' : 'scenario.scope'
		// needs proper error handling
		const publishResponse = await GQLprojects.publishVersion(cmsScenario.databaseId as string, newBlob,
			fileName, externalData, recipientGroupIds, createVersion, publishType, resourcePermissions);
	
		console.info({
			cms_scenario_id: cmsScenario.databaseId, groups: recipientGroupIds,
			create_version: createVersion, extensions_version: extensionsVersion,
			publish_type: publishType, response: publishResponse
		}, "received response for requesting the CMS to publish a project file");
	
		const publish_info = {
			sdk_version : config.CREATE_VERSION,
			published_at: new Date().toISOString(),
			session_type : publishType,
			user_info : store.getState().userModule.sessionInfo as any, //TODO user_info is of type IPublishUserInfo, but sessionInfo is of type IUserSession. they only share the email field, is that the only thing required?
			size: blob.size,
		};
	
		store.dispatch(StoreActions.scenario.setScenarioPublishInfo( publish_info ));
	}
	
	static publishOffline = async (blob: Blob, assetsIds: string[] = [], publishType, additionalBlobMap = {}, encrypted = false) =>
	{
		const currentScenario = Scenario.current();
		if(!currentScenario)
		{
			throw new Error("No current scenario found for publishing");
		}
	
		const projectId = currentScenario.id;
		const description = 'WL Create 2.0 Scenario';
		const createVersion = config.CREATE_VERSION;
		const extensionsVersion = config.SCOPE_EXTENSION_VERSION || '2.0.0';
		const projectName = currentScenario.name || 'No name';
		const addedMetadata = Metadata.calculateProjectMetadata(blob.size) || {};
	
		if (typeof publishType === 'string') 
		{
			publishType = parseInt(publishType);
		}

		const project = await GQLprojects.getProjectByID({ databaseId: projectId });
		if (!project) 
		{
			throw new Error('Project not found');
		}
	
		let cmsScenario: GQLscenario;
	
		if (project.scenarios && project.scenarios.length > 0) 
		{
			cmsScenario = project.scenarios[0];
		}
		else 
		{
			cmsScenario = await this.createScenario(project.id, project.name as string, description);
	
			if (!cmsScenario?.databaseId) 
			{
				throw new Error('There was an error creating the scenario');
			}
		}
	   // create metadata file
		const metaDataFile = this.createMetadataFile(
			projectName, blob.size, createVersion, extensionsVersion, publishType, addedMetadata
		);

		// move glb to a temporary location
		const scenarioId = nanoid();

		const blobWriter = new BlobWriter("application/zip");
		const viewer = await GQLSession.getViewer(); 
		const zipWriteOptions = { 
			bufferedWrite: false,
			password:encrypted ? viewer.encryptionKey || undefined : undefined,
			level: 8
		};
		const zipWriter = new ZipWriter(blobWriter, zipWriteOptions);
		await GQLScenario.addFileToZip(zipWriter, assetsIds, additionalBlobMap, metaDataFile, blob);
		await zipWriter.close();
		const newBlob = await blobWriter.getData();

		const publish_info = {
			sdk_version : config.CREATE_VERSION,
			published_at: new Date().toISOString(),
			session_type : publishType,
			user_info : store.getState().userModule.sessionInfo as any, //TODO user_info is of type IPublishUserInfo, but sessionInfo is of type IUserSession. they only share the email field, is that the only thing required?
			size: newBlob.size,
		};
		store.dispatch(StoreActions.scenario.setScenarioPublishInfo( publish_info ));
				
		return {
			status: 'success',
			url: URL.createObjectURL(newBlob),
			filename: `${scenarioId}.scope`
		};
	}

	static async addFileToZip(zipWriter: ZipWriter<Blob>, assetsIds: string[], additionalBlobMap: object, metaDataFile: string, blob: Blob)
	{
		zipWriter.add('scenario.glb', new BlobReader(blob));

		// extract UI files and insert into the zip
		const htmlFiles = await this.getStepUIData();
		
		//add ui files to zip
		for (const [key, value] of htmlFiles.entries()) 
		{
			await zipWriter.add(`${key}/index.html`, new TextReader(value));
		}
		
		// Gather additional files to be saved to the .scope file
		for (const fileName of Object.keys(additionalBlobMap)) 
		{
			zipWriter.add(`${fileName}`, new BlobReader(additionalBlobMap[fileName]));
		}
				
		// download all assets that need to be included in the .scope file
		if (assetsIds) 
		{
			for (const assetId of assetsIds) 
			{
				const asset = await GQLassets.getAssetForDownload(assetId, "");
	
				if (asset) 
				{
					const fileExtension = asset.name?.split('.').pop();
	
					switch (asset.type) 
					{
						case AssetTypeEnum.Video:
						case AssetTypeEnum.Document:
						case AssetTypeEnum.Audio:
						case AssetTypeEnum.Image:
						case AssetTypeEnum.Model:
							const downloadBlob = await this.downloadFileToBlob(asset.originalFile?.fileUrl);
							await zipWriter.add(`${asset.databaseId}.${fileExtension?.toLowerCase()}`, new BlobReader(downloadBlob));
							break;
						default:
							break;
					}
				}
			}
		}

		await zipWriter.add('scope.meta', new TextReader(metaDataFile));
	}
}
